/**
 * CachePolicy — controls freshness, garbage collection, and dedup behavior.
 *
 * Every consumer (form-engine observer, view-engine DataNode, etc.) can pass
 * a partial policy to override defaults on a per-request basis.
 */

export interface CachePolicy {
  /** Time in ms the cached result is considered fresh. Default: 60_000 (1 min). */
  staleTime: number;
  /** Time in ms to keep entry in memory after last access. Default: 300_000 (5 min). */
  gcTime: number;
  /**
   * Deduplication: if an identical request is already in-flight, piggyback on it
   * rather than firing a new HTTP call. Set to `false` to force a new request.
   * Default: true.
   */
  dedupe: boolean;
}

export const DEFAULT_POLICY: Readonly<CachePolicy> = {
  staleTime: 60_000,
  gcTime: 300_000,
  dedupe: true,
};

/** Immutable data (country lists, static lookups). Never re-fetches once loaded. */
export const IMMUTABLE_POLICY: Readonly<CachePolicy> = {
  staleTime: Infinity,
  gcTime: Infinity,
  dedupe: true,
};

/** Short-lived cache for data that changes frequently but is safe to share briefly. */
export const SHORT_LIVED_POLICY: Readonly<CachePolicy> = {
  staleTime: 5_000,
  gcTime: 30_000,
  dedupe: true,
};
